import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, KeyIcon } from 'lucide-react';
import { AdminApproval } from '../../components/common/AdminApproval';
interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: 'active' | 'inactive' | 'locked';
  lastLogin: string;
  createdAt: string;
}
export function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [pendingAction, setPendingAction] = useState<'add' | 'edit' | 'delete' | 'reset' | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    role: '',
    status: 'active' as 'active' | 'inactive' | 'locked',
    password: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  // Sample data
  useEffect(() => {
    setRoles(['Administrator', 'Manager', 'Operator', 'Auditor']);
    setUsers([{
      id: 1,
      firstName: 'John',
      lastName: 'Doe',
      email: 'john.doe@example.com',
      phone: '+1234567890',
      role: 'Administrator',
      status: 'active',
      lastLogin: '2023-05-10 09:45:22',
      createdAt: '2023-01-15'
    }, {
      id: 2,
      firstName: 'Jane',
      lastName: 'Smith',
      email: 'jane.smith@example.com',
      phone: '+1987654321',
      role: 'Manager',
      status: 'active',
      lastLogin: '2023-05-09 14:30:10',
      createdAt: '2023-01-20'
    }, {
      id: 3,
      firstName: 'Michael',
      lastName: 'Johnson',
      email: 'michael.johnson@example.com',
      phone: '+1122334455',
      role: 'Operator',
      status: 'inactive',
      lastLogin: '2023-04-25 11:15:45',
      createdAt: '2023-02-05'
    }, {
      id: 4,
      firstName: 'Sarah',
      lastName: 'Williams',
      email: 'sarah.williams@example.com',
      phone: '+1555666777',
      role: 'Auditor',
      status: 'locked',
      lastLogin: '2023-04-15 16:20:30',
      createdAt: '2023-03-10'
    }]);
  }, []);
  const filteredUsers = users.filter(user => user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) || user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) || user.email.toLowerCase().includes(searchTerm.toLowerCase()) || user.role.toLowerCase().includes(searchTerm.toLowerCase()));
  const handleAddUser = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: roles[0],
      status: 'active',
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowAddForm(true);
  };
  const handleEditUser = (user: User) => {
    setCurrentUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      phone: user.phone,
      role: user.role,
      status: user.status,
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowEditForm(true);
  };
  const handleDeleteUser = (user: User) => {
    setCurrentUser(user);
    setShowDeleteConfirm(true);
  };
  const handleResetPassword = (user: User) => {
    setCurrentUser(user);
    setFormData({
      ...formData,
      password: '',
      confirmPassword: ''
    });
    setPasswordError('');
    setShowResetPassword(true);
  };
  const validateForm = () => {
    if ((showAddForm || showResetPassword) && formData.password !== formData.confirmPassword) {
      setPasswordError('Passwords do not match');
      return false;
    }
    if ((showAddForm || showResetPassword) && formData.password.length < 8) {
      setPasswordError('Password must be at least 8 characters long');
      return false;
    }
    setPasswordError('');
    return true;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (showAddForm) {
      setPendingAction('add');
    } else if (showEditForm) {
      setPendingAction('edit');
    } else if (showResetPassword) {
      setPendingAction('reset');
    }
    setShowApprovalModal(true);
    setShowAddForm(false);
    setShowEditForm(false);
    setShowResetPassword(false);
  };
  const handleApprove = () => {
    if (pendingAction === 'add') {
      const newUser: User = {
        id: users.length + 1,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: formData.status,
        lastLogin: 'Never',
        createdAt: new Date().toISOString().split('T')[0]
      };
      setUsers([...users, newUser]);
    } else if (pendingAction === 'edit' && currentUser) {
      const updatedUsers = users.map(user => user.id === currentUser.id ? {
        ...user,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        status: formData.status
      } : user);
      setUsers(updatedUsers);
    } else if (pendingAction === 'delete' && currentUser) {
      const updatedUsers = users.filter(user => user.id !== currentUser.id);
      setUsers(updatedUsers);
    }
    // For reset password, we would typically call an API
    setShowApprovalModal(false);
    setPendingAction(null);
    setCurrentUser(null);
    setShowDeleteConfirm(false);
  };
  const handleReject = () => {
    setShowApprovalModal(false);
    setPendingAction(null);
    setShowDeleteConfirm(false);
  };
  const confirmDelete = () => {
    setPendingAction('delete');
    setShowDeleteConfirm(false);
    setShowApprovalModal(true);
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'locked':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="space-y-6" data-id="element-547">
      <div className="flex justify-between items-center" data-id="element-548">
        <h1 className="text-2xl font-bold text-[#1A1F36]" data-id="element-549">User Management</h1>
        <button onClick={handleAddUser} className="flex items-center px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-550">
          <PlusIcon size={16} className="mr-2" data-id="element-551" />
          Add New User
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4" data-id="element-552">
        <div className="mb-4 relative" data-id="element-553">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-554">
            <SearchIcon size={16} className="text-gray-400" data-id="element-555" />
          </div>
          <input type="text" placeholder="Search users..." className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-556" />
        </div>
        <div className="overflow-x-auto" data-id="element-557">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-558">
            <thead className="bg-gray-50" data-id="element-559">
              <tr data-id="element-560">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-561">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-562">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-563">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-564">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-565">
                  Last Login
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-566">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-567">
              {filteredUsers.map(user => <tr key={user.id} data-id="element-568">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-569">
                    <div className="flex items-center" data-id="element-570">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-571">
                        <UserIcon size={20} className="text-gray-500" data-id="element-572" />
                      </div>
                      <div className="ml-4" data-id="element-573">
                        <div className="text-sm font-medium text-gray-900" data-id="element-574">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-575">
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-576">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-577">
                    {user.role}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-578">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(user.status)}`} data-id="element-579">
                      {user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-580">
                    {user.lastLogin}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-581">
                    <div className="flex space-x-2" data-id="element-582">
                      <button onClick={() => handleEditUser(user)} className="text-blue-600 hover:text-blue-900" title="Edit User" data-id="element-583">
                        <EditIcon size={16} data-id="element-584" />
                      </button>
                      <button onClick={() => handleResetPassword(user)} className="text-yellow-600 hover:text-yellow-900" title="Reset Password" data-id="element-585">
                        <KeyIcon size={16} data-id="element-586" />
                      </button>
                      <button onClick={() => handleDeleteUser(user)} className="text-red-600 hover:text-red-900" title="Delete User" data-id="element-587">
                        <TrashIcon size={16} data-id="element-588" />
                      </button>
                    </div>
                  </td>
                </tr>)}
              {filteredUsers.length === 0 && <tr data-id="element-589">
                  <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500" data-id="element-590">
                    No users found
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
      {/* Add User Form */}
      {showAddForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-591">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6" data-id="element-592">
            <div className="flex justify-between items-center mb-4" data-id="element-593">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-594">
                Add New User
              </h3>
              <button onClick={() => setShowAddForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-595">
                <XCircleIcon size={20} data-id="element-596" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-597">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-598">
                <div data-id="element-599">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-600">
                    First Name
                  </label>
                  <input type="text" id="firstName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.firstName} onChange={e => setFormData({
                ...formData,
                firstName: e.target.value
              })} data-id="element-601" />
                </div>
                <div data-id="element-602">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-603">
                    Last Name
                  </label>
                  <input type="text" id="lastName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.lastName} onChange={e => setFormData({
                ...formData,
                lastName: e.target.value
              })} data-id="element-604" />
                </div>
              </div>
              <div className="mb-4" data-id="element-605">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-606">
                  Email
                </label>
                <div className="relative" data-id="element-607">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-608">
                    <MailIcon size={16} className="text-gray-400" data-id="element-609" />
                  </div>
                  <input type="email" id="email" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} data-id="element-610" />
                </div>
              </div>
              <div className="mb-4" data-id="element-611">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-612">
                  Phone
                </label>
                <div className="relative" data-id="element-613">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-614">
                    <PhoneIcon size={16} className="text-gray-400" data-id="element-615" />
                  </div>
                  <input type="text" id="phone" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} data-id="element-616" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-617">
                <div data-id="element-618">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-619">
                    Role
                  </label>
                  <select id="role" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} data-id="element-620">
                    {roles.map(role => <option key={role} value={role} data-id="element-621">
                        {role}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-622">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-623">
                    Status
                  </label>
                  <select id="status" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'active' | 'inactive' | 'locked'
              })} data-id="element-624">
                    <option value="active" data-id="element-625">Active</option>
                    <option value="inactive" data-id="element-626">Inactive</option>
                    <option value="locked" data-id="element-627">Locked</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-628">
                <div data-id="element-629">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-630">
                    Password
                  </label>
                  <input type="password" id="password" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.password} onChange={e => setFormData({
                ...formData,
                password: e.target.value
              })} data-id="element-631" />
                </div>
                <div data-id="element-632">
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-633">
                    Confirm Password
                  </label>
                  <input type="password" id="confirmPassword" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.confirmPassword} onChange={e => setFormData({
                ...formData,
                confirmPassword: e.target.value
              })} data-id="element-634" />
                </div>
              </div>
              {passwordError && <div className="mb-4 text-red-500 text-sm" data-id="element-635">{passwordError}</div>}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-636">
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-637">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-638">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Edit User Form */}
      {showEditForm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-639">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6" data-id="element-640">
            <div className="flex justify-between items-center mb-4" data-id="element-641">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-642">
                Edit User
              </h3>
              <button onClick={() => setShowEditForm(false)} className="text-gray-500 hover:text-gray-700" data-id="element-643">
                <XCircleIcon size={20} data-id="element-644" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-645">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-646">
                <div data-id="element-647">
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-648">
                    First Name
                  </label>
                  <input type="text" id="firstName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.firstName} onChange={e => setFormData({
                ...formData,
                firstName: e.target.value
              })} data-id="element-649" />
                </div>
                <div data-id="element-650">
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-651">
                    Last Name
                  </label>
                  <input type="text" id="lastName" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.lastName} onChange={e => setFormData({
                ...formData,
                lastName: e.target.value
              })} data-id="element-652" />
                </div>
              </div>
              <div className="mb-4" data-id="element-653">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-654">
                  Email
                </label>
                <div className="relative" data-id="element-655">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-656">
                    <MailIcon size={16} className="text-gray-400" data-id="element-657" />
                  </div>
                  <input type="email" id="email" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} data-id="element-658" />
                </div>
              </div>
              <div className="mb-4" data-id="element-659">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-660">
                  Phone
                </label>
                <div className="relative" data-id="element-661">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-662">
                    <PhoneIcon size={16} className="text-gray-400" data-id="element-663" />
                  </div>
                  <input type="text" id="phone" required className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} data-id="element-664" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-665">
                <div data-id="element-666">
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-667">
                    Role
                  </label>
                  <select id="role" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.role} onChange={e => setFormData({
                ...formData,
                role: e.target.value
              })} data-id="element-668">
                    {roles.map(role => <option key={role} value={role} data-id="element-669">
                        {role}
                      </option>)}
                  </select>
                </div>
                <div data-id="element-670">
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-671">
                    Status
                  </label>
                  <select id="status" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.status} onChange={e => setFormData({
                ...formData,
                status: e.target.value as 'active' | 'inactive' | 'locked'
              })} data-id="element-672">
                    <option value="active" data-id="element-673">Active</option>
                    <option value="inactive" data-id="element-674">Inactive</option>
                    <option value="locked" data-id="element-675">Locked</option>
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3" data-id="element-676">
                <button type="button" onClick={() => setShowEditForm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-677">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-678">
                  Update User
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Reset Password Form */}
      {showResetPassword && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-679">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-680">
            <div className="flex justify-between items-center mb-4" data-id="element-681">
              <h3 className="text-lg font-semibold text-[#1A1F36]" data-id="element-682">
                Reset Password
              </h3>
              <button onClick={() => setShowResetPassword(false)} className="text-gray-500 hover:text-gray-700" data-id="element-683">
                <XCircleIcon size={20} data-id="element-684" />
              </button>
            </div>
            <form onSubmit={handleSubmit} data-id="element-685">
              <div className="mb-4" data-id="element-686">
                <p className="text-gray-600 mb-4" data-id="element-687">
                  Reset password for {currentUser?.firstName}{' '}
                  {currentUser?.lastName}
                </p>
              </div>
              <div className="mb-4" data-id="element-688">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-689">
                  New Password
                </label>
                <input type="password" id="password" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.password} onChange={e => setFormData({
              ...formData,
              password: e.target.value
            })} data-id="element-690" />
              </div>
              <div className="mb-4" data-id="element-691">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-692">
                  Confirm New Password
                </label>
                <input type="password" id="confirmPassword" required className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#F58220] focus:border-transparent" value={formData.confirmPassword} onChange={e => setFormData({
              ...formData,
              confirmPassword: e.target.value
            })} data-id="element-693" />
              </div>
              {passwordError && <div className="mb-4 text-red-500 text-sm" data-id="element-694">{passwordError}</div>}
              <div className="mt-6 flex justify-end space-x-3" data-id="element-695">
                <button type="button" onClick={() => setShowResetPassword(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-696">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-[#F58220] text-white rounded-md hover:bg-[#e07619]" data-id="element-697">
                  Reset Password
                </button>
              </div>
            </form>
          </div>
        </div>}
      {/* Delete Confirmation */}
      {showDeleteConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-id="element-698">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6" data-id="element-699">
            <div className="flex items-center mb-4 text-red-600" data-id="element-700">
              <AlertCircleIcon size={24} className="mr-2" data-id="element-701" />
              <h3 className="text-lg font-semibold" data-id="element-702">Delete User</h3>
            </div>
            <p className="text-gray-600 mb-4" data-id="element-703">
              Are you sure you want to delete the user "{currentUser?.firstName}{' '}
              {currentUser?.lastName}"? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3" data-id="element-704">
              <button onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50" data-id="element-705">
                Cancel
              </button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-706">
                Delete
              </button>
            </div>
          </div>
        </div>}
      {/* Admin Approval Modal */}
      <AdminApproval isVisible={showApprovalModal} onClose={() => setShowApprovalModal(false)} onApprove={handleApprove} onReject={handleReject} title={pendingAction === 'add' ? 'Approve New User' : pendingAction === 'edit' ? 'Approve User Update' : pendingAction === 'delete' ? 'Approve User Deletion' : 'Approve Password Reset'} description={pendingAction === 'add' ? `Please review and approve the creation of the new user "${formData.firstName} ${formData.lastName}".` : pendingAction === 'edit' ? `Please review and approve the updates to the user "${formData.firstName} ${formData.lastName}".` : pendingAction === 'delete' ? `Please review and approve the deletion of the user "${currentUser?.firstName} ${currentUser?.lastName}".` : `Please review and approve the password reset for "${currentUser?.firstName} ${currentUser?.lastName}".`} data-id="element-707" />
    </div>;
}